/**
 * iframeStore.js
 * Zustand store for iframe visibility state.
 *
 * Lifted OUT of SceneContent so that opening/closing an iframe does NOT
 * trigger a React re-render of the 3D scene tree (which would cause
 * EarthSurfaceMarker's Drei <Html> to re-project at a stale Earth rotation).
 *
 * Only the components that actually need iframe state subscribe here:
 *  - WebGPUCanvas  → reads `showIframe` for the `paused` prop
 *  - DSL_IframeWindow render site → reads all fields
 *  - EarthSurfaceMarker sits inside the 3D scene and never subscribes here
 */
import { create } from 'zustand'

let _closeTimer = null

export const useIframeStore = create((set) => ({
  showIframe: false,
  iframeUrl: '',
  iframeTitle: '',
  isClosingIframe: false,
  // Separate from showIframe — only goes true after the render loop has
  // resumed and Drei <Html> transforms are up-to-date (managed by FrameLoopController)
  markersVisible: true,
  // Incremented each time markers become visible again — used as the key prop
  // on Drei <Html> to force a fresh re-projection after the loop warms up.
  htmlKey: 0,
  setMarkersVisible: (v) => set((state) => ({
    markersVisible: v,
    htmlKey: v ? state.htmlKey + 1 : state.htmlKey,
  })),

  // Idle-pause: set true after 10 s of no interaction (tablet portrait only).
  // Managed by IdleDetector in WebGPUCanvas.jsx.
  isIdlePaused: false,
  setIdlePaused: (v) => set({ isIdlePaused: v }),

  openIframe: (url, title = '') => {
    if (_closeTimer !== null) {
      clearTimeout(_closeTimer)
      _closeTimer = null
    }
    set({ iframeUrl: url, iframeTitle: title, isClosingIframe: false, showIframe: true })
  },

  closeIframe: () => {
    set({ isClosingIframe: true })
    _closeTimer = setTimeout(() => {
      _closeTimer = null
      set({ showIframe: false, isClosingIframe: false, iframeUrl: '' })
    }, 400)
  },
}))
